<script>
import { mapGetters } from 'vuex';

export default {
  name: 'TheNavBar',
  data() {
    return {
      // local reactive copy used by the template
      breadcrumbs: this.$route.meta?.breadcrumbs || [],
      collapsed: null,
      showNotifications: false,
    };
  },
  computed: {
    ...mapGetters({
      accessLevel: 'user/accessLevel',
      oemCount: 'admin/pendingUserCount',
      offline: 'ui/offline',
      notifications: 'user/notifications',
    }),
    plain() {
      return this.offline || !this.$route.matched.some(route => route.meta.navbar);
    },
    oem() {
      return !this.plain && this.accessLevel === 'OEM';
    },
    notificationCount() {
      return this.notifications ? this.notifications.length : 0;
    },
    staging() {
      return !!import.meta.env.VITE_STAGING;
    },
  },
  watch: {
    // keep watching route.meta.breadcrumbs and update local copy
    '$route.meta.breadcrumbs': {
      handler(newVal) {
        this.breadcrumbs = newVal || [];
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    // ensure local copy seeded
    this.breadcrumbs = this.$route.meta?.breadcrumbs || [];

    // listen for the temporary global event your page dispatches
    this._onBreadcrumbsUpdated = (e) => {
      if (e?.detail) {
        this.breadcrumbs = e.detail;
      }
    };
    window.addEventListener('breadcrumbs-updated', this._onBreadcrumbsUpdated);
  },
  beforeUnmount() {
    window.removeEventListener('breadcrumbs-updated', this._onBreadcrumbsUpdated);
  },
  methods: {
    logout() {
      this.$store.dispatch('user/logout')
        .then(() => this.$router.push('/'));
    },
  },
};
</script>

<template>
  <BNavbar
    expand="md"
    class="mb-2 navbar-dark bg-primary"
  >
    <BNavbarToggle
      v-if="!plain"
      target="nav-collapse"
    />

    <BNavbarBrand
      to="/loggers"
    >
      PMGateway <span v-if="staging">(STAGING)</span>
    </BNavbarBrand>

    <BCollapse
      v-if="!plain"
      id="nav-collapse"
      is-nav
    >
      <BNavbarNav>
        <BNavItem>
          <BBreadcrumb class="bg-primary mh-100">
            <!-- use local breadcrumbs array and a safer key and fallback -->
            <BBreadcrumbItem
              v-for="(bc, i) in breadcrumbs"
              :key="bc.to ?? bc.text ?? i"
            >
              <BLink
                :to="bc.to"
                variant="light"
                underline-opacity="0"
                underline-opacity-hover="100"
              >
                {{ bc.text || '—' }}
              </BLink>
            </BBreadcrumbItem>
          </BBreadcrumb>
        </BNavItem>
      </BNavbarNav>

      <BNavbarNav
        v-if="!plain"
        class="ms-auto"
      >
        <!-- OEM -->
        <BTooltip>
          <template #target>
            <BNavItem
              v-if="oem"
              id="nav-oem-btn"
              to="/oem"
              role="link"
            >
              <FaIcon icon="wrench" />
              <span class="d-md-none ms-4">OEM Panel</span>
              <BBadge
                v-if="oemCount && oemCount > 0"
                class="ms-1"
              >
                {{ oemCount }}
              </BBadge>
            </BNavItem>
          </template>
          OEM Panel
        </BTooltip>

        <!-- Notifications -->
        <BNavItem
          id="nav-notifications-btn"
          role="button"
          @click="showNotifications = true"
        >
          <FaIcon icon="bell" />
          <span class="d-md-none ms-4">Notifications</span>
          <BBadge
            v-if="notificationCount > 0"
            bg="light"
            class="ms-1"
          >
            {{ notificationCount }}
          </BBadge>
        </BNavItem>

        <BTooltip
          target="nav-notifications-btn"
          triggers="hover focus"
          placement="bottom"
        >
          Notifications
        </BTooltip>

        <!-- Help -->
        <BNavItem
          id="nav-help-btn"
          to="/help"
          role="link"
        >
          <FaIcon icon="question-circle" />
          <span class="d-md-none ms-4">Help</span>
        </BNavItem>

        <BTooltip
          target="nav-help-btn"
          triggers="hover focus"
          placement="bottom"
        >
          Help
        </BTooltip>
      </BNavbarNav>

      <BNavbarNav v-if="!plain">
        <BNavItemDropdown right>
          <template #button-content>
            <em>{{ $store.getters['user/name'] }}</em>
          </template>

          <BDropdownItem disabled>
            {{ $store.getters['user/email'] }}
          </BDropdownItem>

          <BDropdownItem disabled>
            {{ $store.getters['user/organisation'] }}
          </BDropdownItem>

          <BDropdownDivider />

          <BDropdownItem to="/profile">
            Profile
          </BDropdownItem>

          <BDropdownItem @click="logout">
            Sign Out
          </BDropdownItem>
        </BNavItemDropdown>
      </BNavbarNav>
    </BCollapse>
  </BNavbar>

  <BModal
    id="notification-modal"
    v-model:visible="showNotifications"
    title="Notifications"
    size="lg"
    no-footer
  >
    <div
      v-if="notificationCount === 0"
      class="text-muted p-3"
    >
      No notifications
    </div>
    <div v-else>
      <ul class="list-unstyled mb-0">
        <li
          v-for="(n, i) in notifications"
          :key="i"
          class="py-2 border-bottom"
        >
          {{ n.title || n.message }}
        </li>
      </ul>
    </div>
  </BModal>
</template>

<style lang="scss">
  .breadcrumb {
    padding: 0 !important;
    margin: 0 !important;
  }

  .breadcrumb .breadcrumb-item + .breadcrumb-item::before {
    color: rgba(255,255,255,0.9);
    opacity: 1;
  }

  .nav-item {
    display: inline;
    float: none;
  }
</style>
